import {
	ActionIcon,
	Badge,
	Button,
	Card,
	Flex,
	SegmentedControl,
	Group,
	Select,
	SimpleGrid,
	Stack,
	Table,
	Text,
	TextInput,
	Tooltip,
} from '@mantine/core'
import {
	IconCash,
	IconCircleCheck,
	IconClockPause,
	IconDownload,
	IconGift,
	IconMessageCircle,
	IconPlayerPlay,
	IconSearch,
	IconUsers,
	IconUserX,
	IconX,
} from '@tabler/icons-react'
import { MRT_ColumnDef, MRT_PaginationState } from 'mantine-react-table'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import {
	CourseAiLearnerFilterOptions,
	CourseAiLearnerQuery,
	CourseAiLearnerRow,
	CourseAiLearnersSummary,
	exportCourseAiLearners,
	getCourseAiLearnerFilterOptions,
	getCourseAiLearners,
	getCourseAiLearnersSummary,
	unwrapObject,
} from 'src/apis/courseAiAPI'
import TableComponent from 'src/components/TableComponent'
import LearnerRefundsPanel from './LearnerRefundsPanel'
import LearnerRevenueModal from './LearnerRevenueModal'
import { KickDialog, StaffCodesBox, canKick } from './LearnerStaffCodes'
import LearnerSupportChatDrawer from './LearnerSupportChatDrawer'
import RevenueDateRangeField from './RevenueDateRangeField'
import {
	COURSE_STATUS_LABELS,
	OFF_DAY_FILTER_OPTIONS,
	PAYMENT_STATUS_FILTER_OPTIONS,
	PAYMENT_TYPE_LABELS,
	buildLearnersCsv,
	completeDaysText,
	daysOffText,
	downloadCsv,
	formatDateTime,
	formatVnd,
	freeTrialText,
	hasLearnerHistory,
	learnerHistoryLines,
	learnerTypeOptions,
	LIST_COUNT_NOTE,
	listCountText,
	nationalityText,
	paymentStatusText,
} from './learnerDisplay'
import { RevenueDateRange } from './revenueDateRange'

type Section = 'learners' | 'refunds'

type Filters = Required<Pick<CourseAiLearnerQuery,
	'search' | 'course_id' | 'level' | 'off_days' | 'course_status' | 'payment_method' | 'payment_status' | 'learner_type' | 'from' | 'to'>>

const EMPTY_FILTERS: Filters = {
	search: '',
	course_id: '',
	level: '',
	off_days: '',
	course_status: '',
	payment_method: '',
	payment_status: '',
	learner_type: 'all',
	from: '',
	to: '',
}

const STATUS_COLORS: Record<string, string> = {
	on_going: 'green',
	inactive: 'yellow',
	cancel: 'red',
	complete: 'blue',
}

const STAFF_CODE_COLORS: Record<string, string> = {
	TUTOR: 'grape',
	PURCHASED: 'cyan',
}

interface CardColors {
	accent: string
	background: string
	border: string
	number: string
}

const CARD_TEXT = '#495057'

/** Tinted background, left accent and icon in the card's color, the number in a dark shade of it. */
const CARD_COLORS: Record<'total' | 'ongoing' | 'inactive' | 'cancelled' | 'completed' | 'trial' | 'revenue', CardColors> = {
	total: { accent: '#52705f', background: '#f2f6f3', border: '#d5e0d8', number: '#24382c' },
	ongoing: { accent: '#16a34a', background: '#ebfbee', border: '#b2f2bb', number: '#166534' },
	inactive: { accent: '#ca8a04', background: '#fff9db', border: '#ffec99', number: '#854d0e' },
	cancelled: { accent: '#dc2626', background: '#fff5f5', border: '#ffc9c9', number: '#991b1b' },
	completed: { accent: '#2563eb', background: '#e7f5ff', border: '#a5d8ff', number: '#1e40af' },
	trial: { accent: '#9333ea', background: '#f8f0fc', border: '#eebefa', number: '#6b21a8' },
	revenue: { accent: '#0f766e', background: '#e6fcf5', border: '#96f2d7', number: '#115e59' },
}

/** MRT `size` per column, so the table fits in about 2000 px; each comes out 24 px wider (cell padding). */
const COLUMN_SIZES: Record<string, number> = {
	no: 28,
	name: 140,
	phone: 118,
	nationality: 84,
	location: 112,
	course_name: 72,
	level: 68,
	payment_date: 84,
	amount_paid: 92,
	payments_count: 66,
	pay: 78,
	complete_days: 96,
	days_off: 60,
	last_active: 84,
	status: 100,
	free_trial: 168,
}

const errorMessage = (error: any, fallback: string) =>
	error?.response?.data?.message ||
	error?.response?.data?.results?.message ||
	fallback

function StatCard({
	label,
	value,
	hint,
	icon: Icon,
	colors,
	onClick,
}: {
	label: string
	value: React.ReactNode
	hint?: React.ReactNode
	icon: React.ElementType
	colors: CardColors
	onClick?: () => void
}) {
	return (
		<Card
			withBorder
			radius="md"
			padding="md"
			onClick={onClick}
			sx={{
				background: colors.background,
				borderColor: colors.border,
				borderLeft: `4px solid ${colors.accent}`,
				cursor: onClick ? 'pointer' : 'default',
				':hover': onClick ? { filter: 'brightness(0.98)' } : undefined,
			}}
		>
			<Flex align="flex-start" justify="space-between" gap={10}>
				<Stack spacing={2} sx={{ minWidth: 0 }}>
					<Text size="xs" tt="uppercase" fw={700} sx={{ color: CARD_TEXT }}>
						{label}
					</Text>
					<Text fw={700} size="xl" sx={{ lineHeight: 1.2, color: colors.number }}>
						{value}
					</Text>
					{hint ? (
						<Text size="xs" sx={{ color: CARD_TEXT }}>
							{hint}
						</Text>
					) : null}
				</Stack>
				<Icon size={22} color={colors.accent} />
			</Flex>
		</Card>
	)
}

function LearnerManagementPanel() {
	const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS)
	const [searchInput, setSearchInput] = useState('')
	const [pagination, setPagination] = useState<MRT_PaginationState>({ pageIndex: 1, pageSize: 20 })
	const [rows, setRows] = useState<CourseAiLearnerRow[]>([])
	const [total, setTotal] = useState(0)
	const [loading, setLoading] = useState(false)
	const [summary, setSummary] = useState<CourseAiLearnersSummary | null>(null)
	const [options, setOptions] = useState<CourseAiLearnerFilterOptions | null>(null)
	const [section, setSection] = useState<Section>('learners')
	const [revenueOpen, setRevenueOpen] = useState(false)
	const [exporting, setExporting] = useState(false)
	const [chatLearner, setChatLearner] = useState<CourseAiLearnerRow | null>(null)
	const [kickLearner, setKickLearner] = useState<CourseAiLearnerRow | null>(null)

	// Search is applied a moment after typing stops, not on every key.
	useEffect(() => {
		const handle = setTimeout(() => {
			const search = searchInput.trim()
			setFilters((previous) => {
				if (previous.search === search) return previous
				setPagination((page) => ({ ...page, pageIndex: 1 }))
				return { ...previous, search }
			})
		}, 400)
		return () => clearTimeout(handle)
	}, [searchInput])

	useEffect(() => {
		getCourseAiLearnerFilterOptions()
			.then((response) => setOptions(unwrapObject<CourseAiLearnerFilterOptions>(response)))
			.catch(() => setOptions(null))
	}, [])

	const loadRows = useCallback(async () => {
		setLoading(true)
		try {
			const response = await getCourseAiLearners({
				...filters,
				page: pagination.pageIndex,
				limit: pagination.pageSize,
			})
			const data = unwrapObject<{ rows: CourseAiLearnerRow[]; count: number }>(response)
			setRows(data?.rows || [])
			setTotal(Number(data?.count) || 0)
		} catch (error) {
			toast.error(errorMessage(error, 'Could not load learners'))
			setRows([])
			setTotal(0)
		} finally {
			setLoading(false)
		}
	}, [filters, pagination.pageIndex, pagination.pageSize])

	const loadSummary = useCallback(async () => {
		try {
			const response = await getCourseAiLearnersSummary(filters)
			setSummary(unwrapObject<CourseAiLearnersSummary>(response))
		} catch (error) {
			setSummary(null)
		}
	}, [filters])

	useEffect(() => {
		loadRows()
	}, [loadRows])

	useEffect(() => {
		loadSummary()
	}, [loadSummary])

	const setFilter = (key: keyof Filters) => (value: string | null) => {
		setPagination((previous) => ({ ...previous, pageIndex: 1 }))
		setFilters((previous) => ({ ...previous, [key]: value || '' }))
	}

	// The revenue range sets from and to together; clearing it empties both.
	const setRevenueRange = (range: RevenueDateRange) => {
		setPagination((previous) => ({ ...previous, pageIndex: 1 }))
		setFilters((previous) => ({ ...previous, from: range.from, to: range.to }))
	}

	// A recorded refund changes the revenue, the cards and the learner's row.
	const reloadAfterRefund = () => {
		loadRows()
		loadSummary()
	}

	// UD-627: a kicked person's rows change (no access any more), so the list is read again.
	const reloadAfterKick = () => {
		setKickLearner(null)
		loadRows()
		loadSummary()
	}

	const handleExport = async () => {
		setExporting(true)
		try {
			const response = await exportCourseAiLearners(filters)
			const data = unwrapObject<{ rows: CourseAiLearnerRow[]; truncated: boolean }>(response)
			const exportRows = data?.rows || []
			downloadCsv(buildLearnersCsv(exportRows), `course-ai-learners-${new Date().toISOString().slice(0, 10)}.csv`)
			if (data?.truncated) {
				toast.warning(`Only the first ${exportRows.length} learners were exported. Narrow the filters to export the rest.`)
			}
		} catch (error) {
			toast.error(errorMessage(error, 'Export failed'))
		} finally {
			setExporting(false)
		}
	}

	const allColumns = useMemo<MRT_ColumnDef<CourseAiLearnerRow>[]>(
		() => [
			{ accessorKey: 'no', header: 'No.' },
			{
				accessorKey: 'name',
				header: 'Learner Name',
				Cell: ({ row }) => (
					<Stack spacing={2} align="flex-start">
						<Text fw={600}>{row.original.name || '-'}</Text>
						{row.original.staff_code ? (
							<Badge size="sm" variant="light" color={STAFF_CODE_COLORS[row.original.staff_code] || 'gray'}>
								{row.original.staff_code}
							</Badge>
						) : null}
						{row.original.refunded ? (
							<Badge size="sm" variant="light" color="red">
								Refunded
							</Badge>
						) : null}
					</Stack>
				),
			},
			{ accessorKey: 'phone', header: 'Phone', Cell: ({ row }) => row.original.phone || '-' },
			{
				accessorKey: 'nationality',
				header: 'Nationality',
				Cell: ({ row }) => nationalityText(row.original.nationality),
			},
			{ accessorKey: 'location', header: 'Location', Cell: ({ row }) => row.original.location || '-' },
			{ accessorKey: 'course_name', header: 'Course', Cell: ({ row }) => row.original.course_name || '-' },
			{ accessorKey: 'level', header: 'Level' },
			{
				id: 'payment_date',
				header: 'Payment Date',
				Cell: ({ row }) => formatDateTime(row.original.latest_payment?.paid_at),
			},
			{
				id: 'amount_paid',
				header: 'Amount Paid',
				Cell: ({ row }) => {
					const payment = row.original.latest_payment
					if (!payment) return '-'
					return (
						<Stack spacing={0}>
							<Text>{formatVnd(payment.amount)}</Text>
							<Text size="xs" color="dimmed">
								{PAYMENT_TYPE_LABELS[payment.type] || payment.type}
							</Text>
						</Stack>
					)
				},
			},
			{
				id: 'payments_count',
				header: 'Payments',
				Cell: ({ row }) => row.original.payments_count,
			},
			{
				id: 'pay',
				header: 'Pay',
				Cell: ({ row }) => row.original.latest_payment?.method_label || '-',
			},
			{
				id: 'complete_days',
				header: 'Complete Days',
				Cell: ({ row }) => completeDaysText(row.original),
			},
			{ id: 'days_off', header: 'Days Off', Cell: ({ row }) => daysOffText(row.original) },
			{
				id: 'last_active',
				header: 'Last Active',
				Cell: ({ row }) => formatDateTime(row.original.last_active_at),
			},
			{
				id: 'status',
				header: 'Status',
				Cell: ({ row }) => (
					<Stack spacing={2}>
						<Badge color={STATUS_COLORS[row.original.course_status] || 'gray'} variant="light">
							{COURSE_STATUS_LABELS[row.original.course_status] || row.original.course_status}
						</Badge>
						<Text size="xs" color="dimmed">
							{paymentStatusText(row.original)}
						</Text>
					</Stack>
				),
			},
			{ id: 'free_trial', header: 'Free 3 days', Cell: ({ row }) => freeTrialText(row.original) },
		],
		[],
	)

	const columns = useMemo(
		() =>
			allColumns.map((column) => {
				const size = COLUMN_SIZES[String(column.id || column.accessorKey)]
				return size ? { ...column, size, minSize: size } : column
			}),
		[allColumns],
	)

	// Payments and recorded refunds in date order; a refund is its own line.
	const renderPayments = ({ row }: { row: { original: CourseAiLearnerRow } }) => (
		<Table fontSize="sm" striped sx={{ maxWidth: 980, margin: '8px 16px' }}>
			<thead>
				<tr>
					<th>#</th>
					<th>Date</th>
					<th>Type</th>
					<th>Amount</th>
					<th>Pay</th>
					<th>Transaction Ref</th>
					<th>Note</th>
				</tr>
			</thead>
			<tbody>
				{learnerHistoryLines(row.original).map((line, index) => (
					<tr key={line.key}>
						<td>{index + 1}</td>
						<td>{formatDateTime(line.date)}</td>
						<td>
							{line.kind === 'refund' ? (
								<Badge size="sm" variant="light" color="red">
									{line.type}
								</Badge>
							) : (
								line.type
							)}
						</td>
						<td style={{ color: line.kind === 'refund' ? '#c92a2a' : undefined }}>{formatVnd(line.amount)}</td>
						<td>{line.method || '-'}</td>
						<td>
							<Text size="xs" color="dimmed">
								{line.transaction_ref}
							</Text>
						</td>
						<td>
							<Text size="xs">{line.note || '-'}</Text>
						</td>
					</tr>
				))}
			</tbody>
		</Table>
	)

	const share = (value?: { count: number; percent: number }) =>
		value ? `${value.count} (${value.percent}%)` : '-'

	const hasFilters = (Object.keys(filters) as (keyof Filters)[]).some(
		(key) => filters[key] !== EMPTY_FILTERS[key],
	)

	// With refunds the card shows what is left, and says what came in and what went back.
	const revenue = summary?.revenue
	const refundCount = revenue?.refund_count || 0
	const revenueHint = revenue
		? `${revenue.transactions} payments, ${revenue.resume_transactions} resume fees.${
				refundCount
					? ` Gross ${formatVnd(revenue.total)}, refunds ${formatVnd(revenue.refund_total)} (${refundCount}).`
					: ''
			} Click for months and days`
		: undefined

	const sectionSwitch = (
		<SegmentedControl
			value={section}
			onChange={(value) => setSection(value as Section)}
			data={[
				{ value: 'learners', label: 'Learners' },
				{ value: 'refunds', label: 'Refunds' },
			]}
			sx={{ alignSelf: 'flex-start' }}
		/>
	)

	if (section === 'refunds') {
		return (
			<Stack spacing="md">
				{sectionSwitch}
				<LearnerRefundsPanel onChanged={reloadAfterRefund} />
			</Stack>
		)
	}

	return (
		<Stack spacing="md">
			{sectionSwitch}
			{/* UD-627: the two staff codes, each changed on its own. */}
			<StaffCodesBox />
			<Flex gap={10} wrap="wrap" align="flex-end">
				<TextInput
					label="Search"
					placeholder="Name or phone"
					icon={<IconSearch size={16} />}
					value={searchInput}
					onChange={(event) => setSearchInput(event.currentTarget.value)}
					sx={{ width: 220 }}
				/>
				<Select
					label="Course"
					placeholder="All courses"
					clearable
					searchable
					data={(options?.courses || []).map((course) => ({ value: course.id, label: course.name || course.id }))}
					value={filters.course_id || null}
					onChange={setFilter('course_id')}
					sx={{ width: 200 }}
				/>
				<Select
					label="Level"
					placeholder="All levels"
					clearable
					data={(options?.levels || ['Beginners']).map((level) => ({ value: level.toLowerCase(), label: level }))}
					value={filters.level || null}
					onChange={setFilter('level')}
					sx={{ width: 140 }}
				/>
				<Select
					label="Off Days"
					placeholder="Any"
					clearable
					data={OFF_DAY_FILTER_OPTIONS}
					value={filters.off_days || null}
					onChange={setFilter('off_days')}
					sx={{ width: 120 }}
				/>
				<Select
					label="Course Status"
					placeholder="Any"
					clearable
					data={Object.entries(COURSE_STATUS_LABELS).map(([value, label]) => ({ value, label }))}
					value={filters.course_status || null}
					onChange={setFilter('course_status')}
					sx={{ width: 150 }}
				/>
				<Select
					label="Payment Method"
					placeholder="Any"
					clearable
					data={[
						...(options?.payment_methods || []),
						{ value: 'none', label: 'Not recorded' },
					]}
					value={filters.payment_method || null}
					onChange={setFilter('payment_method')}
					sx={{ width: 160 }}
				/>
				<Select
					label="Payment Status"
					placeholder="Any"
					clearable
					data={PAYMENT_STATUS_FILTER_OPTIONS}
					value={filters.payment_status || null}
					onChange={setFilter('payment_status')}
					sx={{ width: 150 }}
				/>
				<Select
					label="Learner Type"
					data={learnerTypeOptions(options?.learner_types)}
					value={filters.learner_type || EMPTY_FILTERS.learner_type}
					onChange={(value) => setFilter('learner_type')(value || EMPTY_FILTERS.learner_type)}
					sx={{ width: 170 }}
				/>
				<RevenueDateRangeField value={{ from: filters.from, to: filters.to }} onChange={setRevenueRange} />
				{hasFilters && (
					<Button
						variant="subtle"
						color="gray"
						leftIcon={<IconX size={16} />}
						onClick={() => {
							setSearchInput('')
							setPagination((previous) => ({ ...previous, pageIndex: 1 }))
							setFilters(EMPTY_FILTERS)
						}}
					>
						Clear
					</Button>
				)}
				<Button
					ml="auto"
					variant="outline"
					color="green"
					leftIcon={<IconDownload size={16} />}
					loading={exporting}
					onClick={handleExport}
				>
					Export CSV
				</Button>
			</Flex>

			<SimpleGrid
				cols={7}
				breakpoints={[
					{ maxWidth: 1400, cols: 4 },
					{ maxWidth: 'md', cols: 2 },
					{ maxWidth: 'xs', cols: 1 },
				]}
			>
				<StatCard
					label="Total Learners"
					value={summary?.total_learners ?? '-'}
					hint="Paid only, no trials, no staff"
					icon={IconUsers}
					colors={CARD_COLORS.total}
				/>
				<StatCard label="Ongoing" value={share(summary?.ongoing)} icon={IconPlayerPlay} colors={CARD_COLORS.ongoing} />
				<StatCard label="Inactive" value={share(summary?.inactive)} icon={IconClockPause} colors={CARD_COLORS.inactive} />
				<StatCard label="Cancelled" value={share(summary?.cancelled)} icon={IconX} colors={CARD_COLORS.cancelled} />
				<StatCard label="Completed" value={share(summary?.completed)} icon={IconCircleCheck} colors={CARD_COLORS.completed} />
				<StatCard
					label="Free trial"
					value={summary?.free_trial.total ?? '-'}
					hint={
						summary
							? `${summary.free_trial.on_trial} on trial, ${summary.free_trial.trial_ended} ended, ${summary.free_trial.paid_after_trial} paid after`
							: undefined
					}
					icon={IconGift}
					colors={CARD_COLORS.trial}
				/>
				<StatCard
					label={refundCount ? 'Net Revenue' : 'Total Revenue'}
					value={revenue ? formatVnd(refundCount ? revenue.net_total ?? revenue.total : revenue.total) : '-'}
					hint={revenueHint}
					icon={IconCash}
					colors={CARD_COLORS.revenue}
					onClick={() => setRevenueOpen(true)}
				/>
			</SimpleGrid>

			{/* UD-620: the result of the filters, where the eye looks for it; the cards are another count. */}
			<Flex gap={10} align="baseline" wrap="wrap" data-testid="learners-list-count">
				<Text fw={600}>{loading ? 'Loading the list...' : listCountText(total, hasFilters)}</Text>
				<Text size="sm" color="dimmed">
					{LIST_COUNT_NOTE}
				</Text>
			</Flex>

			<TableComponent
				columns={columns}
				total={total}
				data={rows}
				pagination={pagination}
				loading={loading}
				syncQueryUrl={false}
				handleChangePage={(page: number) => setPagination((previous) => ({ ...previous, pageIndex: page }))}
				handleChangePageSize={(pageSize: number) => setPagination({ pageIndex: 1, pageSize })}
				renderDetailPanel={renderPayments}
				// UD-621: the payments open at once, also on a page that is not on screen.
				detailPanelWithoutAnimation
				hasSubTable={(row: any) => hasLearnerHistory(row?.original)}
				hideMoreActions
				actionColumnSize={132}
				_renderCustomActions={(row: any) => (
					<Group spacing={6} noWrap>
						<Tooltip label="Open support chat" withinPortal>
							<ActionIcon
								variant="light"
								color="green"
								onClick={() => setChatLearner(row.original)}
								aria-label="Open support chat"
							>
								<IconMessageCircle size={16} />
							</ActionIcon>
						</Tooltip>
						{/* UD-627: only on the row of a person who joined with a staff code. A paying learner has Refund with the lock. */}
						{canKick(row.original) ? (
							<Button
								size="xs"
								compact
								variant="outline"
								color="red"
								leftIcon={<IconUserX size={14} />}
								onClick={() => setKickLearner(row.original)}
							>
								Kick
							</Button>
						) : null}
					</Group>
				)}
			/>

			<LearnerRevenueModal opened={revenueOpen} onClose={() => setRevenueOpen(false)} filters={filters} />

			<LearnerSupportChatDrawer learner={chatLearner} onClose={() => setChatLearner(null)} />

			<KickDialog learner={kickLearner} onClose={() => setKickLearner(null)} onDone={reloadAfterKick} />
		</Stack>
	)
}

export default LearnerManagementPanel
